// TODO: add unit tests - https://tasks.awstcprojects.com/browse/PLATFORM-5701
/* istanbul ignore file */
import { TypedDocumentNode } from "@apollo/client";
import type { FC } from "react";
import { MetaDataInterface, PrefetchQueryInterface } from "../interceptors";
import { RemoteComponentInfo } from "./RemoteComponent";

/**
 * These Props are common among all Live types. They are reserved for particular uses and behave slightly differently than
 * the flexible interfaces defined by the Component. For example cascading props via query param overrides may not work as expected.
 */
export type ReservedLiveProps = {
  /**
   * Data that is meant to inform the 'subtype' of a component. Created to handle a case where it is more convenient
   * to have a single XPS with different types vs. an XPS for each type (e.g. There is a checkout MFE, which is almost
   * entirely the same except it serves two different applications, and thus has 'flavors' of 'Skillbuilder' and 'Cloud Institute').
   */
  readonly flavor: string;

  /**
   * An escape hatch to pass a jsonified payload to a live component. This should only be used in cases where having the live component
   * fetch the data itself isn't feasible (e.g. in the sign-in flow the initial values for profile are not in the SuperGraph yet and
   * are instead coming from the IDP).
   */
  readonly payload: string;
};

/**
 * The upper and lower bounds of all props for "Live" components. Individual components should be declaring their own interfaces but they
 * have to respect these constraints.
 */
type LowerBound = Partial<ReservedLiveProps & MetaDataInterface & PrefetchQueryInterface>;

export type UpperBound = LowerBound &
  Record<
    string,
    | string
    | boolean
    | string[]
    | number
    | number[]
    | (() => void)
    | (() => string)
    | ((eventMessage?: string) => void)
    | ((eventMessage?: string) => string)
    | MetaDataInterface["metaData"]
    | PrefetchQueryInterface["prefetchQueryCallback"]
    | undefined
  >;

export { UpperBound as LivePropsUpperBound };

// TODO version bump and change this back to defaulting to LowerBound to force type hygiene
export type LiveComponentSourceProps<T extends UpperBound = UpperBound> = Partial<T & LowerBound>;
export type LiveComponentSource<T extends UpperBound = UpperBound> = FC<LiveComponentSourceProps<T>>;
export type LiveSourceType = "live" | "page";

export type LiveInterceptorFactoryInfo = Omit<RemoteComponentInfo, "endpoint">;
/**
 * Creates an interceptor that is applied on the remote FC before being consumed
 * by the host. Each Live type should have an implementation.
 *
 * @param request the request params passed from the LC's consumer
 * @param Response the React FC from the live component source
 * @returns the response to use, with any upstream interceptions applied
 */
export type LiveInterceptorFactory<T extends UpperBound = UpperBound> = (
  request: LiveInterceptorFactoryInfo,
  response: FC<T>
) => FC<T>;

type InterceptorChain<T extends UpperBound = UpperBound> = Array<
  LiveInterceptorFactory<LowerBound> | LiveInterceptorFactory<T & UpperBound>
>;
/**
 * @deprecated - This is now an alias for LiveInterceptorFactory as that name is more easily understood
 */
export type LiveInterceptor<T extends UpperBound = UpperBound> = LiveInterceptorFactory<T>;

export type VendedLiveSource<Component = FC<UpperBound>> = Component & {
  __interceptorChain?: InterceptorChain;
  __additionalProps?: any;
  __lcType: LiveSourceType;
};

export const vendLiveComponentSource = <
  Props extends UpperBound,
  TQuery extends TypedDocumentNode<any, any> = TypedDocumentNode<any, any>,
>(
  Component: LiveComponentSource<Props>,
  {
    interceptorChain = [],
    ...injected
  }: { interceptorChain?: InterceptorChain<UpperBound> } & PrefetchQueryInterface<Props, TQuery> = {}
) => {
  const VendedComponent: any = Component;
  VendedComponent.__interceptorChain = interceptorChain;
  VendedComponent.__additionalProps = injected;
  VendedComponent.__lcType = "live";
  return VendedComponent;
};

export const vendPageComponentSource = <
  Props extends UpperBound,
  TQuery extends TypedDocumentNode<any, any> = TypedDocumentNode<any, any>,
>(
  Component: LiveComponentSource<Props>,
  {
    interceptorChain = [],
    ...injected
  }: {
    interceptorChain?: InterceptorChain<Props>;
  } & MetaDataInterface &
    PrefetchQueryInterface<Props, TQuery> = {}
) => {
  const VendedComponent: any = Component;
  VendedComponent.__interceptorChain = interceptorChain;
  VendedComponent.__additionalProps = injected;
  VendedComponent.__lcType = "page";
  return VendedComponent;
};

/**
 * @deprecated - LivePropsUpperBound is the more descriptive type.
 * Keeping this for backwards compatibility for now as it is often referenced.
 */
export type LcCustomProps = UpperBound;
